import MuiFullScreenModal from 'app/design/uiComponents/MuiFullScreenModal';
import { TradesType } from '../types';
import { Box } from '@mui/material';
import MuiButton from 'app/design/uiComponents/MuiButton';
import TradeIdeasTradeDetails from './TradeIdeasTradeDetails';
import { useState } from 'react';
import TradeIdeasBotSelection from './TradeIdeasBotSelection';
import { BotSignalParams, GetAllBots } from 'types/ApiServicesTypes';
import TradeIdeasConfirmation from './TradeIdeasConfirmation';
import usePostBotSignal from 'services/Bots/usePostBotSignal';
import { GetInstrumentsResult, Instrument } from 'types/Instrument';
import { SYMBOL, findInstrumentsBySymbol } from '../utils';
import { v4 as uuid } from 'uuid';
import useGetAllBots from 'services/Bots/useGetAllBots';
// import moment from 'moment';
// import { getValueFromLocalStorage } from 'app/components/Bots/BotSignal/util';
// import { SIGNAL_LEVERAGE } from 'constants/common';

type TradeDetailsCardProps = {
  open: boolean;
  handleClose: () => void;
  tradeData: TradesType;
  trade_type: string;
  lotSize: number;
  handleLotsize: (lots: number) => void;
  symbol: string;
  expiry: string;
  instruments: GetInstrumentsResult;
};
const TradeExecutionModal = ({
  open,
  handleClose,
  tradeData,
  trade_type,
  handleLotsize,
  lotSize,
  symbol,
  expiry,
  instruments,
}: TradeDetailsCardProps) => {
  const [step, setStep] = useState(0);
  const { data: signalBots, refetch } = useGetAllBots(1, undefined, 'line');
  const [bId, setBId] = useState(0);
  const mutation = usePostBotSignal(bId);

  // Check if instruments data is loaded and has the expected structure
  if (!instruments || !Array.isArray(instruments) || instruments.length === 0) {
    return (
      <MuiFullScreenModal
        open={open}
        width="470px"
        handleClose={handleClose}
        body={
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <p>Loading instruments data...</p>
            <p>Please wait while we fetch the required data.</p>
          </Box>
        }
      />
    );
  }

  // Check if we have instruments for the selected symbol
  const symbolInstruments = instruments.filter(instrument => 
    instrument.name === SYMBOL[symbol as keyof typeof SYMBOL] || 
    instrument.trading_symbol.includes(SYMBOL[symbol as keyof typeof SYMBOL] || '')
  );
  
  if (symbolInstruments.length === 0) {
    return (
      <MuiFullScreenModal
        open={open}
        width="470px"
        handleClose={handleClose}
        body={
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <p>No instruments found for {symbol}</p>
            <p>Please try again or contact support if the issue persists.</p>
            <p>Available symbols: {Object.keys(SYMBOL).join(', ')}</p>
          </Box>
        }
      />
    );
  }

  const handleClick = (bot: GetAllBots) => {
    setStep(step => step + 1);
    setBId(bot.botid);
  };
  const instrumentsList = tradeData.positions.reduce((acc, curr) => {
    // Use the utility function for better instrument filtering
    const filteredInstruments = findInstrumentsBySymbol(
      instruments,
      symbol,
      curr.strike,
      curr.contract_type,
      expiry
    );

    // Add null check to prevent undefined access
    if (filteredInstruments.length > 0) {
      acc[curr.strike] = filteredInstruments[0];
    }

    return acc;
  }, {} as Record<string, Instrument>);

  const signalsData: Array<BotSignalParams> = tradeData.positions.map(
    position => {
      const instrument = instrumentsList[position.strike];

      // Add null check before accessing instrument properties
      if (!instrument) {
        throw new Error(
          `Instrument not found for strike: ${position.strike}. Please ensure instruments data is loaded correctly.`,
        );
      }

      return {
        signal_id: uuid(),
        instrument_id: instrument.instrument_id.toString(),
        symbol: instrument.trading_symbol,
        product_type: 'MIS',
        order_type: 'MARKET',
        trade_id: '234',
        transaction_type: position.direction === 'long' ? 'BUY' : 'SELL',
        order_duration: 'DAY',
        quantity: lotSize * instrument.lot_size,
        tag: '234 EN',
        position_type: 'EN',
        validity: '',
        order_price: '',
      };
    },
  );

  return (
    <MuiFullScreenModal
      open={open}
      width="470px"
      handleClose={handleClose}
      body={
        <>
          {step === 0 && (
            <TradeIdeasTradeDetails
              handleLotsize={handleLotsize}
              tradeData={tradeData}
              trade_type={trade_type}
              lotSize={lotSize}
              symbol={symbol}
            />
          )}
          {step === 1 && (
            <TradeIdeasBotSelection
              signalBotsData={signalBots?.data?.result || []}
              handleClick={handleClick}
              handleCreateBot={success => {
                if (success) void refetch();
              }}
            />
          )}
          {step === 2 && (
            <TradeIdeasConfirmation
              lotSize={lotSize}
              tradeData={tradeData}
              trade_type={trade_type}
              symbol={symbol}
            />
          )}
        </>
      }
      footer={
        <Box sx={{ textAlign: 'end' }}>
          <MuiButton
            variant="contained"
            sx={{
              bgcolor: 'primary.main',
              mx: 1,
            }}
            onClick={() => {
              if (step === 0) handleClose();
              else setStep(step => step - 1);
            }}
          >
            {step === 0 ? 'Cancel' : 'Back'}
          </MuiButton>
          {step !== 1 && (
            <MuiButton
              variant="contained"
              sx={{
                bgcolor: '#007DFF',
                '&:hover': {
                  bgcolor: '#4c98fc',
                },
              }}
              onClick={() => {
                if (step !== 2) {
                  setStep(step => step + 1);
                } else {
                  mutation.mutate(
                    { signals: signalsData },
                    {
                      onSuccess: res => {
                        if (res.success) {
                          handleClose();
                        }
                      },
                      onError: error => {
                        // Handle error silently or show user-friendly message
                      }
                    },
                  );
                }
              }}
            >
              {step === 0 ? 'Trade' : 'Confirm'}
            </MuiButton>
          )}
        </Box>
      }
    />
  );
};

export default TradeExecutionModal;
